import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Notification, useLogin, useNotify, useTheme } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import "./Login.scss";
import { PublicClientApplication } from "@azure/msal-browser";
import MicrosoftLogo from "../../Components/Icons/MicrosoftLogo";
import { microsoftAuth } from "../../Utils/RestUtils";
import jwtDecode from "jwt-decode";

const Login = ({}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const location = useLocation();
  const [referrer, setReferrer] = useState("");
  const [theme] = useTheme();
  const year = new Date().getFullYear();
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: process.env.REACT_APP_MICROSOFT_AUTH_CLIENT_ID,
      authority:
        "https://login.microsoftonline.com/" +
        process.env.REACT_APP_MICROSOFT_AUTH_TENANT_ID,
      redirectUri: process.env.REACT_APP_MICROSOFT_AUTH_REDIRECT_URI,
    },
  });

  useEffect(() => {
    const { nextPathname } = location.state || { nextPathname: referrer };
    setReferrer(nextPathname);
  }, []);

  const handleStandardLogin = (e) => {
    setLoading(true);
    e.preventDefault();

    login({ email, password }, `${referrer}`)
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        notify("Invalid email or password", { type: "error" });
      });
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    try {
      await msalInstance.initialize();

      const response = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "email"],
      });

      const idToken = response.idToken;
      const result = await microsoftAuth(idToken);

      if (result.data?.token) {
        const decodedToken = jwtDecode(result.data.token);

        localStorage.setItem("token", result.data.token);
        localStorage.setItem("user_id", decodedToken.userID);
        localStorage.setItem("permissions", decodedToken.roles);
        localStorage.setItem("display_name", decodedToken.displayName);
        localStorage.setItem("email", decodedToken.username);
        localStorage.setItem("nice_active", decodedToken.niceActive);

        window.location.href = referrer || "/";
      } else {
        notify(result.data.error + ", " + result.data.details, {
          type: "error",
        });
      }
    } catch (error) {
      console.log(error);
      notify("Microsoft authentication failed", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      sx={{
        height: "100vh",
        marginTop: "0 !important",
        marginBottom: "0 !important",
      }}
    >
      <Grid
        item
        xs={12}
        md={6}
        lg={7}
        sx={{
          backgroundImage: "url('/home-bg.jpg')",
          backgroundSize: "cover",
        }}
      ></Grid>
      <Grid item xs={12} md={6} lg={5}>
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: theme.palette.background.default,
            display: "flex",
            height: "100%",
            padding: "2rem",
          }}
        >
          <Card
            sx={{ maxWidth: 500, margin: "0 auto", padding: "1rem 1rem 2rem" }}
          >
            <CardContent>
              <Typography variant="h4" component="h1" fontWeight="bold">
                SQR US - Login
              </Typography>

              <Button
                size="large"
                onClick={handleMicrosoftLogin}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={14} thickness={3} color="primary" />
                  ) : (
                    <MicrosoftLogo size={20} />
                  )
                }
                fullWidth
                sx={{ mt: 3, mb: 1 }}
              >
                Login with Microsoft
              </Button>

              <Box sx={{ textAlign: "center", mt: 1 }}>
                <Typography
                  component="span"
                  variant="h6"
                  color="text.secondary"
                  onClick={() => setShowEmailForm((v) => !v)}
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                >
                  {showEmailForm ? "Hide email login" : "Login via email"}
                </Typography>
              </Box>

              {showEmailForm && (
                <form onSubmit={handleStandardLogin}>
                  <TextField
                    type="email"
                    name="email"
                    label="Email"
                    value={email}
                    fullWidth
                    margin="dense"
                    size="small"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    type="password"
                    name="password"
                    label="Password"
                    value={password}
                    fullWidth
                    margin="dense"
                    size="small"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Notification />
                  <Button
                    size="large"
                    type="submit"
                    disabled={loading}
                    startIcon={
                      loading && (
                        <CircularProgress
                          size={14}
                          thickness={3}
                          color="primary"
                        />
                      )
                    }
                    sx={{ mt: 1 }}
                    fullWidth
                  >
                    Login
                  </Button>
                </form>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  mt: 3,
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  Important Information
                </Typography>
                <Typography>
                  This is a private system. Authorised usage only.
                </Typography>
                <Typography>
                  All activity is logged and monitored for the purposes of
                  maintaining system security and integrity.
                </Typography>
                <Typography>
                  This system may contain Personally Identifiable Information
                  (PII) and appropriate safeguards must be in place to protect
                  this information.
                </Typography>
                <Typography fontSize="10px">
                  © Copyright {year} ApexAvenue Ltd. All rights reserved.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login;
